export interface User {
  id: number;
  email: string;
  createdAt: string;
}

export interface Device {
  id: number;
  userId: number;
  name: string;
  createdAt: string;
}

export interface SyncLease {
  id: number;
  deviceId: number;
  leaseToken: string;
  expiresAt: string;
}

export interface AuditLog {
  id: number;
  subject: string;
  action: string;
  createdAt: string;
  meta: Record<string, unknown>;
}

const now = new Date();

const addMinutes = (minutes: number) =>
  new Date(now.getTime() + minutes * 60 * 1000).toISOString();

export const mockUsers: User[] = [
  { id: 1, email: 'alice@example.com', createdAt: addMinutes(-60) },
  { id: 2, email: 'bob@example.com', createdAt: addMinutes(-180) },
  { id: 3, email: 'carol@example.com', createdAt: addMinutes(-360) },
];

export const mockDevices: Device[] = [
  { id: 101, userId: 1, name: 'Alice MacBook Pro', createdAt: addMinutes(-55) },
  { id: 102, userId: 2, name: 'Bob iMac', createdAt: addMinutes(-170) },
  { id: 103, userId: 3, name: 'Carol Mac mini', createdAt: addMinutes(-340) },
];

export const mockSyncLeases: SyncLease[] = [
  {
    id: 201,
    deviceId: 101,
    leaseToken: 'lease-101-token',
    expiresAt: addMinutes(120),
  },
  {
    id: 202,
    deviceId: 102,
    leaseToken: 'lease-102-token',
    expiresAt: addMinutes(30),
  },
  {
    id: 203,
    deviceId: 103,
    leaseToken: 'lease-103-token',
    expiresAt: addMinutes(240),
  },
];

export const mockAuditLogs: AuditLog[] = [
  {
    id: 301,
    subject: 'user:1',
    action: 'user.created',
    createdAt: addMinutes(-58),
    meta: { email: 'alice@example.com' },
  },
  {
    id: 302,
    subject: 'device:102',
    action: 'device.registered',
    createdAt: addMinutes(-165),
    meta: { userId: 2, name: 'Bob iMac' },
  },
  {
    id: 303,
    subject: 'lease:203',
    action: 'sync.lease.issued',
    createdAt: addMinutes(-20),
    meta: { deviceId: 103 },
  },
  {
    id: 304,
    subject: 'user:2',
    action: 'user.created',
    createdAt: addMinutes(-178),
    meta: { email: 'bob@example.com' },
  },
  {
    id: 305,
    subject: 'device:101',
    action: 'device.registered',
    createdAt: addMinutes(-52),
    meta: { userId: 1, name: 'Alice MacBook Pro' },
  },
  {
    id: 306,
    subject: 'lease:201',
    action: 'sync.lease.issued',
    createdAt: addMinutes(-45),
    meta: { deviceId: 101 },
  },
  {
    id: 307,
    subject: 'user:3',
    action: 'user.created',
    createdAt: addMinutes(-358),
    meta: { email: 'carol@example.com' },
  },
  {
    id: 308,
    subject: 'device:103',
    action: 'device.registered',
    createdAt: addMinutes(-335),
    meta: { userId: 3, name: 'Carol Mac mini' },
  },
  {
    id: 309,
    subject: 'lease:202',
    action: 'sync.lease.renewed',
    createdAt: addMinutes(-15),
    meta: { deviceId: 102, previousExpiry: addMinutes(-5) },
  },
  {
    id: 310,
    subject: 'user:1',
    action: 'user.updated',
    createdAt: addMinutes(-30),
    meta: { field: 'email', oldValue: 'alice.old@example.com' },
  },
  {
    id: 311,
    subject: 'device:102',
    action: 'device.updated',
    createdAt: addMinutes(-120),
    meta: { field: 'name', oldValue: 'Bob MacBook' },
  },
  {
    id: 312,
    subject: 'lease:201',
    action: 'sync.lease.revoked',
    createdAt: addMinutes(-10),
    meta: { deviceId: 101, reason: 'user_requested' },
  },
  {
    id: 313,
    subject: 'user:4',
    action: 'user.created',
    createdAt: addMinutes(-480),
    meta: { email: 'dave@example.com' },
  },
  {
    id: 314,
    subject: 'device:104',
    action: 'device.registered',
    createdAt: addMinutes(-475),
    meta: { userId: 4, name: 'Dave iPad' },
  },
  {
    id: 315,
    subject: 'device:104',
    action: 'device.deleted',
    createdAt: addMinutes(-5),
    meta: { userId: 4, reason: 'user_deactivation' },
  },
  {
    id: 316,
    subject: 'user:4',
    action: 'user.deleted',
    createdAt: addMinutes(-3),
    meta: { email: 'dave@example.com', reason: 'account_closure' },
  },
  {
    id: 317,
    subject: 'lease:203',
    action: 'sync.lease.renewed',
    createdAt: addMinutes(-100),
    meta: { deviceId: 103 },
  },
  {
    id: 318,
    subject: 'user:2',
    action: 'user.updated',
    createdAt: addMinutes(-90),
    meta: { field: 'password', note: 'password_changed' },
  },
  {
    id: 319,
    subject: 'device:105',
    action: 'device.registered',
    createdAt: addMinutes(-85),
    meta: { userId: 1, name: 'Alice iPhone' },
  },
  {
    id: 320,
    subject: 'lease:204',
    action: 'sync.lease.issued',
    createdAt: addMinutes(-80),
    meta: { deviceId: 105 },
  },
  {
    id: 321,
    subject: 'user:5',
    action: 'user.created',
    createdAt: addMinutes(-600),
    meta: { email: 'eve@example.com' },
  },
  {
    id: 322,
    subject: 'device:106',
    action: 'device.registered',
    createdAt: addMinutes(-595),
    meta: { userId: 5, name: 'Eve Surface' },
  },
  {
    id: 323,
    subject: 'lease:205',
    action: 'sync.lease.issued',
    createdAt: addMinutes(-590),
    meta: { deviceId: 106 },
  },
  {
    id: 324,
    subject: 'lease:205',
    action: 'sync.lease.revoked',
    createdAt: addMinutes(-400),
    meta: { deviceId: 106, reason: 'expired' },
  },
  {
    id: 325,
    subject: 'device:107',
    action: 'device.registered',
    createdAt: addMinutes(-250),
    meta: { userId: 2, name: 'Bob iPad Pro' },
  },
];

const delay = <T>(data: T, ms = 250): Promise<T> =>
  new Promise(resolve => setTimeout(() => resolve(data), ms));

export const fetchUsers = () => delay(mockUsers);
export const fetchDevices = () => delay(mockDevices);
export const fetchSyncLeases = () => delay(mockSyncLeases);
export const fetchAuditLogs = () => delay(mockAuditLogs);

export const fetchStats = async () => {
  const [users, devices, leases, auditLogs] = await Promise.all([
    fetchUsers(),
    fetchDevices(),
    fetchSyncLeases(),
    fetchAuditLogs(),
  ]);

  return {
    usersCount: users.length,
    devicesCount: devices.length,
    leasesExpiringSoon: leases.filter(lease => {
      const expires = new Date(lease.expiresAt).getTime();
      return expires - now.getTime() < 48 * 60 * 60 * 1000;
    }).length,
    auditLast24h: auditLogs.filter(log => {
      const created = new Date(log.createdAt).getTime();
      return now.getTime() - created < 24 * 60 * 60 * 1000;
    }).length,
  };
};
